// react
import { ChangeEvent, useContext } from 'react';

// types
import { DocumentType, Entity } from '../../../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUser, ContextUserEntity } from '../../../../../../App.context';

// mui
import { Box, Checkbox, Grid, Switch, SxProps, Typography } from '@mui/material';

// ui
import { BtnMenu } from '../../../../../ui/BtnMenu/BtnMenu';
import { TypographyOverflow } from '../../../../../ui/TypographyOverflow/TypographyOverflow';

// dialogs
import { DialogDeleteDocumentType } from '../../../../../dialogs/DialogDeleteDocumentType/DialogDeleteDocumentType';
import { DialogEditDocumentType } from '../../../../../dialogs/DialogEditDocumentType/DialogEditDocumentType';

// icons
import {
	DeleteOutlined as IconDelete,
	ModeEditOutlined as IconModeEdit,
} from '@mui/icons-material';

// props
export interface CardDocTypeProps {
	docType: DocumentType;
	hasEntity?: boolean;
	isChecked?: boolean;
	isCheckedPrivate?: boolean;
	isCheckedRequired?: boolean;
	onChange: (e: ChangeEvent<HTMLInputElement>) => void;
	onChangePrivate: (e: ChangeEvent<HTMLInputElement>) => void;
	onChangeRequired: (e: ChangeEvent<HTMLInputElement>) => void;
	onSuccessDelete: () => void;
	onSuccessEdit: () => void;
}

export const CardDocType = ({
	docType,
	hasEntity = false,
	isChecked = false,
	isCheckedPrivate = false,
	isCheckedRequired = false,
	onChange,
	onChangePrivate,
	onChangeRequired,
	onSuccessDelete,
	onSuccessEdit,
}: CardDocTypeProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogDocType = searchParams.get('dialogDocumentType');

	// context
	const { user } = useContext(ContextUser);
	const { t } = useTranslation();
	const { userEntity } = useContext(ContextUserEntity);

	// vars
	const docTypeEntityId =
		docType.entity && typeof docType.entity === 'object' ? docType.entity.id : docType.entity;
	const idDocType = `doc-type-${docType.id}`;
	const isWfa = user?.role === 'WFA';

	let isEditable = false;

	if ((isWfa && !docType.entity) || docTypeEntityId === userEntity?.id) {
		isEditable = true;
	}

	return (
		<>
			<Box
				component="label"
				htmlFor={idDocType}
				sx={(theme) => {
					let sx: SxProps = {
						backgroundColor: theme.palette.brand.grey[100],
						borderColor: 'transparent',
						borderStyle: 'solid',
						borderWidth: 1,
						borderRadius: 2,
						padding: theme.spacing(1, 1.5),
						display: 'flex',
						cursor: 'pointer',
						transition: 'all 200ms ease',
						'&:hover': {
							backgroundColor: theme.palette.brand.grey[200],
						},
					};

					if (isChecked) {
						sx = {
							...sx,
							borderColor: theme.palette.brand.blue[300],
							backgroundColor: theme.palette.brand.blue[200],
							'&:hover': {
								backgroundColor: theme.palette.brand.blue[200],
							},
						};
					}

					return {
						...sx,
					};
				}}>
				<Grid alignItems="center" container spacing={2}>
					<Grid item xs={6}>
						<Grid alignItems="center" container spacing={1}>
							<Grid item>
								<Checkbox
									checked={isChecked}
									disableRipple={true}
									id={idDocType}
									onChange={onChange}
									sx={{ padding: 0 }}
								/>
							</Grid>
							<Grid item flex={1} sx={{ minWidth: 0 }}>
								<Grid container>
									<Grid item xs={12}>
										<Typography fontWeight={500}>{docType.name}</Typography>
									</Grid>
									{hasEntity && docType.entity && (
										<Grid item xs={12}>
											<TypographyOverflow color="brand.grey.500" variant="caption">
												{(docType.entity as Entity).name}
											</TypographyOverflow>
										</Grid>
									)}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid hidden={!isChecked} item xs={2.5}>
						<Switch checked={isCheckedRequired} onChange={onChangeRequired} size="small" />
					</Grid>
					<Grid hidden={!isChecked} item xs={2.5}>
						<Switch checked={isCheckedPrivate} onChange={onChangePrivate} size="small" />
					</Grid>
					{isEditable && (
						<Grid item xs="auto" sx={{ marginLeft: 'auto' }}>
							<BtnMenu
								items={[
									{
										children: t('editDocumentType'),
										icon: <IconModeEdit />,
										onClick: (e) => {
											e.stopPropagation();

											searchParams.set('dialog', 'editDocumentType');
											searchParams.set('dialogDocumentType', String(docType.id));
											setSearchParams(searchParams);
										},
									},
									{
										children: t('deleteDocumentType'),
										disabled: true,
										icon: <IconDelete color="error" />,
										onClick: (e) => {
											e.stopPropagation();

											searchParams.set('dialog', 'deleteDocumentType');
											searchParams.set('dialogDocumentType', String(docType.id));
											setSearchParams(searchParams);
										},
									},
								]}
							/>
						</Grid>
					)}
				</Grid>
			</Box>
			<DialogDeleteDocumentType
				isOpen={paramDialog === 'deleteDocumentType' && paramDialogDocType === String(docType.id)}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogDocumentType');
					setSearchParams(searchParams);
				}}
				onSuccess={onSuccessDelete}
			/>

			<DialogEditDocumentType
				isOpen={paramDialog === 'editDocumentType' && paramDialogDocType === String(docType.id)}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogDocumentType');
					setSearchParams(searchParams);
				}}
				onSuccess={onSuccessEdit}
			/>
		</>
	);
};
